import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./NotificationBell.module.css";
import { Icon } from "./Icon";
import {
  markerAlleLest,
  markerLest,
  tidSiden,
  useVarsler,
  type Varsel,
} from "../lib/notifications";
import {
  hentInnstillinger,
  hentVarsler,
  lagreInnstillinger,
  merkVarslerLest,
  type Innstillinger,
  type ServerVarsel,
} from "../lib/oppgaver";

const IKON: Record<string, string> = { tildelt: "👤", deltaker: "👥", kommentar: "💬", status: "✔", paminnelse: "⏰", dokument: "📄" };

/**
 * Varsler om oppgaver fra serveren (oppg.Varsel), hentet hvert minutt, og brukerens valg for
 * påminnelser og e-post. Uten Oppgaver i API-et (eldre versjon) blir listen bare tom.
 */
function useOppgaveVarsler() {
  const [varsler, setVarsler] = useState<ServerVarsel[]>([]);
  const last = useCallback(() => {
    hentVarsler().then(setVarsler).catch(() => setVarsler([]));
  }, []);
  useEffect(() => {
    last();
    const t = window.setInterval(last, 60_000);
    const synlig = () => document.visibilityState === "visible" && last();
    document.addEventListener("visibilitychange", synlig);
    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", synlig);
    };
  }, [last]);
  return { varsler, setVarsler, last };
}

export function NotificationBell() {
  const { varsler: lokale, ulest: ulestLokale } = useVarsler();
  const oppg = useOppgaveVarsler();
  const [innst, setInnst] = useState<Innstillinger | null>(null);
  // Oppgavevarslene vises som de andre varslene, nyeste først
  const varsler: (Varsel & { serverId?: number })[] = [
    ...lokale,
    ...oppg.varsler.map((v) => ({
      id: `oppg:${v.id}`,
      serverId: v.id,
      type: "info" as const,
      tittel: `${IKON[v.type] ?? "🔔"} ${v.tekst}`,
      lenke: v.oppgaveId ? `/oppgaver?oppgave=${v.oppgaveId}` : "/oppgaver",
      tid: Date.parse(v.opprettet),
      lest: v.lest,
    })),
  ].sort((a, b) => b.tid - a.tid);
  const ulest = ulestLokale + oppg.varsler.filter((v) => !v.lest).length;
  const [apen, setApen] = useState(false);
  useEffect(() => {
    if (apen && !innst) hentInnstillinger().then(setInnst).catch(() => undefined);
  }, [apen, innst]);
  const settInnst = (e: Partial<Innstillinger>) => {
    setInnst((i) => (i ? { ...i, ...e } : i));
    lagreInnstillinger(e).then(setInnst).catch(() => hentInnstillinger().then(setInnst).catch(() => undefined));
  };
  const [pos, setPos] = useState<CSSProperties>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const aapne = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Nederst i sidemenyen (ACL-rammen) åpner panelet oppover; i topplinjen nedover.
      const plassUnder = window.innerHeight - r.bottom;
      const vert = plassUnder > 420 ? { top: r.bottom + 8 } : { bottom: window.innerHeight - r.top + 8 };
      const left = Math.max(12, Math.min(r.left, window.innerWidth - 352));
      setPos({ ...vert, left });
    }
    setApen(true);
  };

  useEffect(() => {
    if (!apen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setApen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setApen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [apen]);

  const klikk = (v: Varsel & { serverId?: number }) => {
    if (v.serverId) {
      oppg.setVarsler((l) => l.map((x) => (x.id === v.serverId ? { ...x, lest: true } : x)));
      merkVarslerLest({ id: v.serverId }).catch(() => undefined);
    } else markerLest(v.id);
    setApen(false);
    if (v.lenke) navigate(v.lenke);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={styles.bjelle}
        onClick={() => (apen ? setApen(false) : aapne())}
        title="Varsler"
        aria-label={ulest ? `Varsler (${ulest} uleste)` : "Varsler"}
      >
        <Icon name="bell" size={18} />
        {ulest > 0 && <span className={styles.badge}>{ulest > 9 ? "9+" : ulest}</span>}
      </button>

      {apen && (
        <div
          ref={panelRef}
          className={styles.panel}
          style={pos}
          role="dialog"
          aria-label="Varsler"
        >
          <div className={styles.hode}>
            <span className={styles.hodeTittel}>Varsler</span>
            {ulest > 0 && (
              <button
                type="button"
                className={styles.merkAlle}
                onClick={() => {
                  markerAlleLest();
                  oppg.setVarsler((l) => l.map((x) => ({ ...x, lest: true })));
                  merkVarslerLest().catch(() => undefined);
                }}
              >
                Merk alle som lest
              </button>
            )}
          </div>

          {varsler.length === 0 ? (
            <p className={styles.tom}>Ingen varsler ennå.</p>
          ) : (
            <ul className={styles.liste}>
              {varsler.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    className={`${styles.item} ${v.lest ? "" : styles.itemUlest}`}
                    onClick={() => klikk(v)}
                  >
                    <span
                      className={styles.prikk}
                      data-lest={v.lest ? "true" : undefined}
                      aria-hidden="true"
                    />
                    <span className={styles.itemKol}>
                      <span className={styles.itemTittel}>{v.tittel}</span>
                      {v.tekst && <span className={styles.itemTekst}>{v.tekst}</span>}
                      <span className={styles.itemTid}>{tidSiden(v.tid)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {innst && (
            <div className={styles.oppsett}>
              <span className={styles.hodeTittel}>Oppgaver</span>
              <label className={styles.bryter}>
                <input type="checkbox" checked={innst.paminnUke} onChange={(e) => settInnst({ paminnUke: e.target.checked })} />
                Påminnelse 7 dager før fristen
              </label>
              <label className={styles.bryter}>
                <input type="checkbox" checked={innst.paminnDag} onChange={(e) => settInnst({ paminnDag: e.target.checked })} />
                Påminnelse samme dag som fristen
              </label>
              <label className={styles.bryter}>
                <input type="checkbox" checked={innst.epost} onChange={(e) => settInnst({ epost: e.target.checked })} />
                Varsler også på e-post
              </label>
              {innst.epost && !innst.epostTilgjengelig && (
                <span className={styles.itemTekst}>E-post er ikke slått på i portalen ennå. Valget ditt er lagret og gjelder når det er på plass.</span>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
